/* In-memory state of the page. Loaded from the database after login; every change is written to the database first. */
import type {Assignment, Client, Entry, Member, Project, Role, Task, Timer} from './lib/model.ts';
import {defaultFilters, type Filters} from './lib/report.ts';

export const State = {
  tab: 'timer',
  meId: null as string | null,            // team-member id linked to this login
  role: null as Role | null,
  members: [] as Member[], clients: [] as Client[], projects: [] as Project[], tasks: [] as Task[], entries: [] as Entry[],
  timers: [] as Timer[],
  assign: [] as Assignment[],              // who may record on which project (owner/admin: everyone's rows; others: their own)
  dataVersion: 0,                          // goes up whenever the server's entries change (reports then ask again)
  entriesLimit: 150,
  rpt: {...defaultFilters(), history: [] as Partial<Filters>[], expanded: new Set<string>(), leafLimit: {} as Record<string, number>,
        selBucket: null as number | null, nodes: [] as {key: string; gkey: string; dim: string; level: number}[], focusQ: false},
};
export const byId = <T extends {id: string}>(arr: T[], id: string | null | undefined): T | undefined => arr.find(x => x.id === id);
export const me = () => byId(State.members, State.meId);
export function memberName(id: string) { return byId(State.members, id)?.name ?? '(unknown member)'; }
export function clientName(id: string) { return byId(State.clients, id)?.name ?? '(unknown client)'; }
export function projectName(id: string) { return byId(State.projects, id)?.name ?? '(unknown project)'; }
export function taskName(id: string) { return id ? (byId(State.tasks, id)?.name ?? '') : ''; }
export const tasksForProject = (pid: string) => State.tasks.filter(t => t.projectId === pid);
export const projectsForClient = (cid: string) => State.projects.filter(p => p.clientId === cid);
export const activeMembers = () => State.members.filter(m => m.active);
export const activeProjects = () => State.projects.filter(p => p.active);
/** owner and admins may record time on every project (also for someone else); members and the bookkeeper only on
    projects assigned to them. The database enforces the same rule (migration 011); this only decides what is offered. */
export const usesAllProjects = () => State.role === 'owner' || State.role === 'admin';
let mine: {src: Assignment[]; me: string | null; set: Set<string>} | null = null;
export function mayRecord(projectId: string | null | undefined): boolean {
  if (!projectId || usesAllProjects()) return true;
  if (!mine || mine.src !== State.assign || mine.me !== State.meId)
    mine = {src: State.assign, me: State.meId, set: new Set((State.assign || []).filter(a => a.memberId === State.meId).map(a => a.projectId))};
  return mine.set.has(projectId);
}
export const NOT_ASSIGNED = "You're not assigned to this project. Ask the owner to add you to it.";
export const ARCHIVED_PROJECT = 'This project is archived — restore it in Clients → Projects to use it again.';
/** archived project, or a project of an archived client: hidden from every pick-list; takes no new time (012 enforces it
    in the database for members and the bookkeeper; owner/admin are only stopped here) */
export function projectArchived(projectId: string | null | undefined): boolean {
  const p = projectId ? byId(State.projects, projectId) : undefined;
  if (!p) return false;
  const c = byId(State.clients, p.clientId);
  return !p.active || (!!c && !c.active);
}
/** why new time cannot go on this project ('' = it can): archived, or not assigned to this person */
export function recordBlock(projectId: string | null | undefined): string {
  if (!projectId) return '';
  if (projectArchived(projectId)) return ARCHIVED_PROJECT;
  return mayRecord(projectId) ? '' : NOT_ASSIGNED;
}
/** people assigned to a project (owner/admin see everyone's assignments) */
export const projectPeople = (projectId: string) => (State.assign || []).filter(a => a.projectId === projectId).map(a => a.memberId);
export function uid(prefix = ''): string { return prefix + crypto.randomUUID(); }
/** redraw the current screen (set by main.ts) */
export const ui = {render: () => {}, reload: async () => {}};   // reload = read everything again from the database
